import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  ArrowLeft, Upload, Link as LinkIcon, Save, Trash2, ArrowUp, ArrowDown, LogOut, Plus, Loader2, FolderOpen, Sparkles, Star, RefreshCw, Wand2, UserSearch,
} from "lucide-react";
import { BUCKETS, BUCKET_MAP } from "../data/buckets";
import {
  fetchMedia, login, createMedia, updateMedia, deleteMedia, uploadFile, bulkUpload, resolveUrl, setHero, reconvertHeic, reconvertStatus, reclassify, reclassifyStatus, scanSolo, scanSoloStatus,
} from "../lib/api";

const TOKEN_KEY = "saloni_admin_token";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [authing, setAuthing] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const data = await fetchMedia();
      setItems(data);
    } catch {
      toast.error("Failed to load media");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) reload();
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthing(true);
    try {
      const t = await login(passcode);
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      toast.success("Welcome in.");
    } catch {
      toast.error("Wrong passcode.");
    } finally {
      setAuthing(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setItems([]);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F7F9FC] flex items-center justify-center p-5">
        <Toaster position="top-center" />
        <div className="w-full max-w-md rounded-3xl bg-white p-8 card-shadow border border-[#EAF0F6]">
          <p className="font-body text-xs uppercase tracking-[0.3em] text-[#7B9ACC]">Family only</p>
          <h1 className="mt-3 font-display font-bold text-3xl text-[#0B192C] tracking-tight">
            Memory Lane admin
          </h1>
          <p className="mt-3 font-body text-sm text-[#334E68]">
            Enter the passcode to add photos, videos, and captions.
          </p>
          <form onSubmit={handleLogin} className="mt-6 space-y-3">
            <input
              data-testid="admin-passcode-input"
              type="password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Passcode"
              className="w-full rounded-2xl border border-[#EAF0F6] px-4 py-3 font-body text-[#0B192C] outline-none focus:border-[#A7C7E7]"
            />
            <button
              data-testid="admin-login-btn"
              disabled={authing}
              className="w-full rounded-full bg-[#0B192C] text-[#F7F9FC] py-3 font-body text-sm disabled:opacity-60"
            >
              {authing ? "Checking…" : "Enter"}
            </button>
          </form>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm text-[#334E68] hover:text-[#0B192C]">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <Toaster position="top-center" />
      {/* Header */}
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-[#F7F9FC]/80 border-b border-[#EAF0F6]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <ArrowLeft className="w-4 h-4 text-[#334E68] group-hover:-translate-x-0.5 transition-transform" />
            <span className="font-display font-semibold text-[#0B192C]">Memory admin</span>
          </Link>
          <button
            data-testid="admin-logout"
            onClick={handleLogout}
            className="rounded-full border border-[#EAF0F6] px-3 py-1.5 text-xs font-body text-[#334E68] hover:bg-white flex items-center gap-2"
          >
            <LogOut className="w-3.5 h-3.5" />
            Log out
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 space-y-10">
        <MaintenancePanel token={token} />
        <ContentPanel token={token} />
        <MusicPanel token={token} />
        <TriviaPanel token={token} />
        <BulkUploadCard token={token} onCreated={reload} />
        <AddNewCard token={token} onCreated={reload} />

        {loading ? (
          <div className="py-16 text-center font-body text-[#334E68]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center font-body text-[#334E68]">No items yet. Add your first above.</div>
        ) : (
          <BucketGroups
            items={items}
            token={token}
            onChanged={reload}
          />
        )}
      </div>
    </div>
  );
}

// Restore original component (accidentally displaced by insertion above)
function MaintenancePanel({ token }) {
  const [status, setStatus] = useState(null);
  const [reStatus, setReStatus] = useState(null);
  const [soloStatus, setSoloStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reBusy, setReBusy] = useState(false);
  const [soloBusy, setSoloBusy] = useState(false);

  const check = async () => {
    try {
      const [s, r, so] = await Promise.all([reconvertStatus(token), reclassifyStatus(token), scanSoloStatus(token)]);
      setStatus(s); setReStatus(r); setSoloStatus(so);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    check();
    const t = setInterval(check, 4000);
    return () => clearInterval(t);
  }, []);

  const start = async () => {
    setBusy(true);
    try {
      await reconvertHeic(token);
      toast.success("HEIC conversion started.");
      check();
    } catch { toast.error("Failed to start conversion."); }
    finally { setBusy(false); }
  };

  const startReclassify = async () => {
    setReBusy(true);
    try {
      await reclassify(token, "both");
      toast.success("AI re-classification started.");
      check();
    } catch { toast.error("Failed to start reclassification."); }
    finally { setReBusy(false); }
  };

  const startSolo = async () => {
    setSoloBusy(true);
    try {
      await scanSolo(token);
      toast.success("Solo photo scan started. Hero rotator will update automatically.");
      check();
    } catch { toast.error("Failed to start solo scan."); }
    finally { setSoloBusy(false); }
  };

  const running = status?.running;
  const reRunning = reStatus?.running;
  const soloRunning = soloStatus?.running;

  return (
    <div className="rounded-3xl bg-white p-5 md:p-6 card-shadow border border-[#EAF0F6] space-y-4" data-testid="maintenance-panel">
      <div>
        <p className="font-body text-xs uppercase tracking-[0.3em] text-[#7B9ACC]">Maintenance</p>
        <h3 className="mt-1 font-display font-bold text-xl text-[#0B192C]">Fix-it kit</h3>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <ToolCard
          title="HEIC → JPEG"
          desc="Convert older iPhone HEIC uploads so browsers can render them."
          status={status && `${status.converted}/${status.total || 0}${status.failed ? ` · ${status.failed} failed` : ""}`}
          running={running || busy}
          onRun={start}
          icon={<RefreshCw className="w-4 h-4" />}
          testid="reconvert-btn"
        />
        <ToolCard
          title="AI re-classify"
          desc="Re-run Claude vision on Milestones + raw titles. Reassigns bucket, writes new title + Hinglish caption."
          status={reStatus && `${reStatus.processed}/${reStatus.total || 0}${reStatus.failed ? ` · ${reStatus.failed} failed` : ""}`}
          running={reRunning || reBusy}
          onRun={startReclassify}
          icon={<Wand2 className="w-4 h-4" />}
          testid="reclassify-btn"
        />
        <ToolCard
          title="Pick hero rotator"
          desc="Scan solo photos of just her. Auto-picks 2 beautiful + 2 funny for the home page rotator."
          status={soloStatus && `${soloStatus.processed}/${soloStatus.total || 0} · ${soloStatus.solo_found || 0} solo found`}
          running={soloRunning || soloBusy}
          onRun={startSolo}
          icon={<UserSearch className="w-4 h-4" />}
          testid="scan-solo-btn"
        />
      </div>
    </div>
  );
}

function ContentPanel({ token }) {
  const [texts, setTexts] = useState({});
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState({});

  useEffect(() => {
    axios.get(`${API}/site-texts`).then((r) => { setTexts(r.data); setValues(r.data); });
  }, []);

  const FIELDS = [
    { key: "reality_body", label: "About Onnii (main body)", rows: 8 },
    { key: "soft_body", label: "Soft Side (paragraph)", rows: 5 },
    { key: "mom_body", label: "Mom Mode (paragraph)", rows: 5 },
    { key: "hero_taglines", label: "Hero rotating taglines (one per line, pipe-separated in DB)", rows: 5, splitPipe: true },
  ];

  const save = async (key) => {
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      let v = values[key] || "";
      if (FIELDS.find((f) => f.key === key)?.splitPipe) {
        v = v.split("\n").map((s) => s.trim()).filter(Boolean).join("|");
      }
      await axios.patch(`${API}/admin/site-texts/${key}`, { value: v }, { headers: { Authorization: `Bearer ${token}` } });
      setTexts((t) => ({ ...t, [key]: v }));
      toast.success("Saved. Refresh the home page to see it.");
    } catch { toast.error("Save failed."); }
    finally { setSaving((s) => ({ ...s, [key]: false })); }
  };

  return (
    <div className="rounded-3xl bg-white p-5 md:p-6 card-shadow border border-[#EAF0F6] space-y-4" data-testid="content-panel">
      <div>
        <p className="font-body text-xs uppercase tracking-[0.3em] text-[#7B9ACC]">Content</p>
        <h3 className="mt-1 font-display font-bold text-xl text-[#0B192C]">Edit site copy</h3>
        <p className="mt-1 font-body text-xs text-[#334E68]">Change the About texts and the hero taglines. Live on next home refresh.</p>
      </div>
      {FIELDS.map((f) => {
        const displayValue = f.splitPipe
          ? (values[f.key] || "").split("|").map((s) => s.trim()).join("\n")
          : (values[f.key] || "");
        const dirty = f.splitPipe
          ? displayValue.split("\n").filter(Boolean).join("|") !== (texts[f.key] || "")
          : displayValue !== (texts[f.key] || "");
        return (
          <div key={f.key} className="rounded-2xl bg-[#F7F9FC] p-4 border border-[#EAF0F6]">
            <label className="block font-body text-xs uppercase tracking-widest text-[#7B9ACC] mb-2">{f.label}</label>
            <textarea
              value={displayValue}
              onChange={(e) => setValues((v) => ({ ...v, [f.key]: f.splitPipe ? e.target.value : e.target.value }))}
              rows={f.rows}
              className="w-full rounded-xl border border-[#EAF0F6] px-3 py-2 font-body text-sm text-[#0B192C] outline-none focus:border-[#A7C7E7] resize-y bg-white"
              data-testid={`content-${f.key}`}
            />
            <div className="mt-2 flex justify-end">
              <button
                onClick={() => save(f.key)}
                disabled={!dirty || saving[f.key]}
                data-testid={`save-${f.key}`}
                className={`rounded-full px-4 py-1.5 text-xs font-body flex items-center gap-1.5 ${dirty ? "bg-[#0B192C] text-[#F7F9FC]" : "bg-[#F0F5FA] text-[#334E68] cursor-not-allowed"}`}
              >
                {saving[f.key] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MusicPanel({ token }) {
  const [settings, setSettings] = useState({});
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");

  useEffect(() => {
    axios.get(`${API}/settings`).then((r) => {
      setSettings(r.data || {});
      setTitle(r.data?.music_title || "");
      setArtist(r.data?.music_artist || "Taylor Swift");
    });
  }, []);

  const uploadAndSet = async () => {
    if (!file) return toast.error("Pick an audio file first.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/settings/upload-music`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      await axios.patch(`${API}/admin/settings`, {
        music_url: data.url, music_title: title || file.name.replace(/\.[^.]+$/, ""), music_artist: artist, music_enabled: true,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Music uploaded and enabled.");
      const r = await axios.get(`${API}/settings`);
      setSettings(r.data);
      setFile(null);
    } catch { toast.error("Upload failed."); }
    finally { setUploading(false); }
  };

  const toggle = async () => {
    const next = !settings.music_enabled;
    await axios.patch(`${API}/admin/settings`, { music_enabled: next }, { headers: { Authorization: `Bearer ${token}` } });
    setSettings((s) => ({ ...s, music_enabled: next }));
    toast.success(next ? "Music enabled." : "Music muted for visitors.");
  };

  return (
    <div className="rounded-3xl bg-white p-5 md:p-6 card-shadow border border-[#EAF0F6] space-y-4" data-testid="music-panel">
      <div>
        <p className="font-body text-xs uppercase tracking-[0.3em] text-[#7B9ACC]">Ambient music</p>
        <h3 className="mt-1 font-display font-bold text-xl text-[#0B192C]">Set a Taylor Swift track</h3>
        <p className="mt-1 font-body text-xs text-[#334E68]">Upload an MP3. The site will loop it softly with a mute pill in the corner. She's a Swiftie — this is her Eras Tour opening.</p>
      </div>
      {settings.music_url && (
        <div className="rounded-2xl bg-[#F7F9FC] p-3 border border-[#EAF0F6] flex items-center gap-3">
          <audio controls src={settings.music_url.startsWith("http") ? settings.music_url : `${process.env.REACT_APP_BACKEND_URL}${settings.music_url}`} className="flex-1" />
          <button onClick={toggle} className="rounded-full px-3 py-1.5 text-xs font-body bg-[#0B192C] text-[#F7F9FC]">
            {settings.music_enabled === false ? "Enable" : "Disable"}
          </button>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-3">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Track title (e.g. 'Lover')" className="rounded-xl border border-[#EAF0F6] px-3 py-2 font-body text-sm" />
        <input value={artist} onChange={(e) => setArtist(e.target.value)} placeholder="Artist" className="rounded-xl border border-[#EAF0F6] px-3 py-2 font-body text-sm" />
      </div>
      <div className="flex gap-3 items-center">
        <input type="file" accept="audio/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm text-[#334E68] file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#0B192C] file:text-[#F7F9FC] file:font-body file:text-sm" data-testid="music-file" />
        <button onClick={uploadAndSet} disabled={!file || uploading} data-testid="music-upload" className="rounded-full bg-[#0B192C] text-[#F7F9FC] px-5 py-2 font-body text-sm flex items-center gap-2 disabled:opacity-60">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Upload & set
        </button>
      </div>
    </div>
  );
}

function TriviaPanel({ token }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState(["", "", "", ""]);
  const [correct, setCorrect] = useState(0);
  const [quip, setQuip] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await axios.get(`${API}/trivia`);
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    const options = opts.map((o) => o.trim()).filter(Boolean);
    if (!q.trim() || options.length < 2) return toast.error("Question + at least 2 options required.");
    if (correct >= options.length) return toast.error("Correct index out of range.");
    setBusy(true);
    try {
      await axios.post(`${API}/admin/trivia`, { question: q, options, correct, quip }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Question added.");
      setQ(""); setOpts(["", "", "", ""]); setCorrect(0); setQuip("");
      load();
    } catch { toast.error("Add failed."); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this question?")) return;
    try { await axios.delete(`${API}/admin/trivia/${id}`, { headers: { Authorization: `Bearer ${token}` } }); load(); }
    catch { toast.error("Delete failed."); }
  };

  return (
    <div className="rounded-3xl bg-white p-5 md:p-6 card-shadow border border-[#EAF0F6] space-y-4" data-testid="trivia-panel">
      <div>
        <p className="font-body text-xs uppercase tracking-[0.3em] text-[#7B9ACC]">Trivia</p>
        <h3 className="mt-1 font-display font-bold text-xl text-[#0B192C]">Add & manage questions</h3>
      </div>

      <div className="rounded-2xl bg-[#F7F9FC] p-4 border border-[#EAF0F6] space-y-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Question" className="w-full rounded-xl border border-[#EAF0F6] px-3 py-2 font-body text-sm" data-testid="trivia-question" />
        {opts.map((o, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input type="radio" checked={correct === i} onChange={() => setCorrect(i)} title="Mark as correct" />
            <input value={o} onChange={(e) => setOpts((prev) => prev.map((x, j) => j === i ? e.target.value : x))} placeholder={`Option ${i + 1}${i === correct ? " ✓" : ""}`} className="flex-1 rounded-xl border border-[#EAF0F6] px-3 py-2 font-body text-sm" data-testid={`trivia-opt-${i}`} />
          </div>
        ))}
        <input value={quip} onChange={(e) => setQuip(e.target.value)} placeholder="Cheeky follow-up line shown after answer" className="w-full rounded-xl border border-[#EAF0F6] px-3 py-2 font-body text-sm" data-testid="trivia-quip" />
        <div className="flex justify-end">
          <button onClick={add} disabled={busy} data-testid="trivia-add" className="rounded-full bg-[#0B192C] text-[#F7F9FC] px-5 py-2 text-sm font-body flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add question
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {items.map((it, idx) => (
          <div key={it.id} className="rounded-2xl bg-[#F7F9FC] p-3 border border-[#EAF0F6] flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-body text-xs text-[#7B9ACC]">Q{idx + 1}</p>
              <p className="font-body text-sm text-[#0B192C] font-medium">{it.question}</p>
              <p className="font-body text-xs text-[#334E68] mt-1">Correct: <b>{it.options[it.correct]}</b></p>
            </div>
            <button onClick={() => del(it.id)} className="p-1.5 rounded-full border border-red-100 text-red-500 hover:bg-red-50" title="Delete">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ToolCard({ title, desc, status, running, onRun, icon, testid }) {
  return (
    <div className="rounded-2xl bg-[#F7F9FC] p-4 border border-[#EAF0F6] flex flex-col justify-between gap-3">
      <div>
        <p className="font-display font-semibold text-[#0B192C]">{title}</p>
        <p className="mt-1 font-body text-xs text-[#334E68]">{desc}</p>
        {status && (
          <p className="mt-2 font-body text-xs text-[#334E68]">
            {running ? "Running… " : ""}
            <b>{status}</b>
          </p>
        )}
      </div>
      <button
        data-testid={testid}
        onClick={onRun}
        disabled={running}
        className="rounded-full bg-[#0B192C] text-[#F7F9FC] px-4 py-2 font-body text-xs flex items-center gap-2 disabled:opacity-60 self-start"
      >
        {running ? <Loader2 className="w-4 h-4 animate-spin" /> : icon}
        {running ? "Working" : "Run"}
      </button>
    </div>
  );
}

function BulkUploadCard({ token, onCreated }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const inputRef = useRef(null);
  const folderRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const pickFiles = (fileList) => {
    const arr = Array.from(fileList || []).filter(
      (f) => f.type.startsWith("image/") || f.type.startsWith("video/")
    );
    setFiles(arr);
    setResults(null);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const items = e.dataTransfer.items;
    if (items && items.length && items[0].webkitGetAsEntry) {
      const collected = [];
      const walk = async (entry) => {
        if (entry.isFile) {
          await new Promise((res) => {
            entry.file((f) => { collected.push(f); res(); });
          });
        } else if (entry.isDirectory) {
          const reader = entry.createReader();
          const entries = await new Promise((res) => reader.readEntries(res));
          for (const child of entries) await walk(child);
        }
      };
      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry?.();
        if (entry) await walk(entry);
      }
      pickFiles(collected);
    } else {
      pickFiles(e.dataTransfer.files);
    }
  };

  const upload = async () => {
    if (!files.length) return;
    setUploading(true);
    setProgress(0);
    setResults(null);
    try {
      // Chunk into batches of 5 so the concurrent Claude calls don't stack up
      const chunks = [];
      for (let i = 0; i < files.length; i += 5) chunks.push(files.slice(i, i + 5));
      let created = 0;
      let failed = 0;
      const allResults = [];
      for (let i = 0; i < chunks.length; i++) {
        const data = await bulkUpload(token, chunks[i]);
        created += data.created || 0;
        failed += data.failed || 0;
        allResults.push(...(data.results || []));
        setProgress(Math.round(((i + 1) / chunks.length) * 100));
      }
      setResults({ created, failed, results: allResults });
      toast.success(`Added ${created} memories${failed ? `, ${failed} failed` : ""}. Review buckets below.`);
      setFiles([]);
      if (inputRef.current) inputRef.current.value = "";
      if (folderRef.current) folderRef.current.value = "";
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-3xl bg-white p-6 md:p-8 card-shadow border border-[#EAF0F6]" data-testid="bulk-upload-card">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2">
            <p className="font-body text-xs uppercase tracking-[0.3em] text-[#7B9ACC]">Bulk upload</p>
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#0B192C] text-[#A7C7E7] font-body">
              <Sparkles className="w-3 h-3" /> AI auto-bucket
            </span>
          </div>
          <h2 className="mt-2 font-display font-bold text-2xl text-[#0B192C] tracking-tight">
            Drop a whole folder in.
          </h2>
          <p className="mt-1 font-body text-sm text-[#334E68] max-w-lg">
            Every photo gets classified into a bucket with a cheeky first-draft
            caption. Videos default to <span className="italic">Certified Chaos</span> — you can fix any of it below.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="bulk-pick-files"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="rounded-full border border-[#EAF0F6] px-4 py-2 text-sm font-body text-[#0B192C] hover:bg-[#F7F9FC] flex items-center gap-2"
          >
            <Upload className="w-4 h-4" /> Pick files
          </button>
          <button
            data-testid="bulk-pick-folder"
            onClick={() => folderRef.current?.click()}
            disabled={uploading}
            className="rounded-full border border-[#EAF0F6] px-4 py-2 text-sm font-body text-[#0B192C] hover:bg-[#F7F9FC] flex items-center gap-2"
          >
            <FolderOpen className="w-4 h-4" /> Pick folder
          </button>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,video/*"
        hidden
        onChange={(e) => pickFiles(e.target.files)}
      />
      <input
        ref={folderRef}
        type="file"
        multiple
        hidden
        webkitdirectory=""
        directory=""
        onChange={(e) => pickFiles(e.target.files)}
      />

      <div
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        data-testid="bulk-dropzone"
        className={`rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${
          dragActive ? "border-[#7B9ACC] bg-[#EAF0F6]" : "border-[#EAF0F6] bg-[#F7F9FC]"
        }`}
      >
        <FolderOpen className="w-8 h-8 mx-auto text-[#7B9ACC] mb-2" />
        <p className="font-body text-sm text-[#334E68]">
          Drag a folder or multiple files here, or click "Pick folder" above.
        </p>
        <p className="mt-1 font-body text-xs text-[#334E68]/70">
          Photos + videos. Nested folders supported. Uploads in batches of 5.
        </p>
      </div>

      {files.length > 0 && (
        <div className="mt-5" data-testid="bulk-preview">
          <div className="flex items-center justify-between mb-3">
            <p className="font-body text-sm text-[#0B192C]">
              <strong>{files.length}</strong> file{files.length === 1 ? "" : "s"} ready
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setFiles([])}
                disabled={uploading}
                className="text-xs font-body text-[#334E68] hover:text-[#0B192C]"
              >
                Clear
              </button>
              <button
                data-testid="bulk-start"
                onClick={upload}
                disabled={uploading}
                className="rounded-full bg-[#0B192C] text-[#F7F9FC] px-5 py-2 text-sm font-body flex items-center gap-2 disabled:opacity-60"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {uploading ? `Uploading… ${progress}%` : `Upload & auto-bucket`}
              </button>
            </div>
          </div>
          {uploading && (
            <div className="mb-3 h-1.5 rounded-full bg-[#EAF0F6] overflow-hidden">
              <div className="h-full bg-[#7B9ACC] transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-64 overflow-auto pr-1">
            {files.slice(0, 60).map((f, i) => (
              <div key={i} className="rounded-lg bg-[#F0F5FA] p-1.5 text-[10px] font-body text-[#334E68] truncate" title={f.name}>
                {f.type.startsWith("video/") ? "🎬 " : "🖼️ "}
                {f.name}
              </div>
            ))}
            {files.length > 60 && (
              <div className="col-span-full text-xs text-[#334E68]/70 pt-1">
                …and {files.length - 60} more
              </div>
            )}
          </div>
        </div>
      )}

      {results && !uploading && (
        <div className="mt-4 rounded-2xl bg-[#E6F0F9] border border-[#D6E4F0] p-4 text-sm font-body text-[#0B192C]" data-testid="bulk-results">
          <p>
            <strong>{results.created}</strong> memories added.{" "}
            {results.failed > 0 && (
              <span className="text-red-600">{results.failed} failed.</span>
            )}
            {" "}Scroll down to fix any bucket / caption you don't love.
          </p>
        </div>
      )}
    </div>
  );
}

function AddNewCard({ token, onCreated }) {
  const [mode, setMode] = useState("upload"); // "upload" | "url"
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [url, setUrl] = useState("");
  const [bucket, setBucket] = useState(BUCKETS[0].key);
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [mediaType, setMediaType] = useState("image");
  const fileRef = useRef(null);

  const reset = () => {
    setFile(null);
    setUrl("");
    setTitle("");
    setCaption("");
    setProgress(0);
    setMediaType("image");
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);
      let payloadUrl = url;
      let storagePath = null;
      let inferredType = mediaType;

      if (mode === "upload") {
        if (!file) return toast.error("Choose a file first.");
        const res = await uploadFile(token, file, setProgress);
        payloadUrl = res.url;
        storagePath = res.storage_path;
        inferredType = res.media_type;
      } else {
        if (!payloadUrl) return toast.error("Paste a URL.");
      }

      await createMedia(token, {
        url: payloadUrl,
        media_type: inferredType,
        bucket,
        title,
        caption,
        storage_path: storagePath,
      });
      toast.success("Added!");
      reset();
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-3xl bg-white p-6 md:p-8 card-shadow border border-[#EAF0F6]">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
        <div>
          <p className="font-body text-xs uppercase tracking-[0.3em] text-[#7B9ACC]">Add memory</p>
          <h2 className="mt-2 font-display font-bold text-2xl text-[#0B192C] tracking-tight">
            Drop in a photo or video
          </h2>
        </div>
        <div className="flex gap-1 p-1 rounded-full bg-[#F7F9FC] border border-[#EAF0F6]">
          <button
            data-testid="mode-upload"
            onClick={() => setMode("upload")}
            className={`px-4 py-1.5 rounded-full text-sm font-body flex items-center gap-1.5 ${
              mode === "upload" ? "bg-[#0B192C] text-[#F7F9FC]" : "text-[#334E68]"
            }`}
          >
            <Upload className="w-3.5 h-3.5" /> Upload
          </button>
          <button
            data-testid="mode-url"
            onClick={() => setMode("url")}
            className={`px-4 py-1.5 rounded-full text-sm font-body flex items-center gap-1.5 ${
              mode === "url" ? "bg-[#0B192C] text-[#F7F9FC]" : "text-[#334E68]"
            }`}
          >
            <LinkIcon className="w-3.5 h-3.5" /> Paste URL
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid md:grid-cols-2 gap-5">
        {mode === "upload" ? (
          <div className="md:col-span-2">
            <label className="block font-body text-xs text-[#334E68] mb-1.5">File (image or video)</label>
            <input
              ref={fileRef}
              data-testid="admin-file-input"
              type="file"
              accept="image/*,video/*"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setFile(f);
                if (f) setMediaType(f.type.startsWith("video/") ? "video" : "image");
              }}
              className="w-full text-sm text-[#334E68] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#0B192C] file:text-[#F7F9FC] file:font-body file:text-sm hover:file:bg-[#334E68]"
            />
            {uploading && (
              <div className="mt-2 h-1.5 rounded-full bg-[#EAF0F6] overflow-hidden">
                <div className="h-full bg-[#7B9ACC] transition-all" style={{ width: `${progress}%` }} />
              </div>
            )}
          </div>
        ) : (
          <div className="md:col-span-2">
            <label className="block font-body text-xs text-[#334E68] mb-1.5">Public URL</label>
            <input
              data-testid="admin-url-input"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://…"
              className="w-full rounded-2xl border border-[#EAF0F6] px-4 py-2.5 font-body text-sm text-[#0B192C] outline-none focus:border-[#A7C7E7]"
            />
            <div className="mt-2 flex gap-2 text-xs">
              <label className="font-body text-[#334E68]">Type:</label>
              <label className="flex items-center gap-1 font-body text-[#334E68]">
                <input type="radio" checked={mediaType === "image"} onChange={() => setMediaType("image")} /> image
              </label>
              <label className="flex items-center gap-1 font-body text-[#334E68]">
                <input type="radio" checked={mediaType === "video"} onChange={() => setMediaType("video")} /> video
              </label>
            </div>
          </div>
        )}

        <div>
          <label className="block font-body text-xs text-[#334E68] mb-1.5">Bucket</label>
          <select
            data-testid="admin-bucket-select"
            value={bucket}
            onChange={(e) => setBucket(e.target.value)}
            className="w-full rounded-2xl border border-[#EAF0F6] px-4 py-2.5 font-body text-sm text-[#0B192C] outline-none focus:border-[#A7C7E7] bg-white"
          >
            {BUCKETS.map((b) => (
              <option key={b.key} value={b.key}>{b.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block font-body text-xs text-[#334E68] mb-1.5">Title (short)</label>
          <input
            data-testid="admin-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Team Nap"
            className="w-full rounded-2xl border border-[#EAF0F6] px-4 py-2.5 font-body text-sm text-[#0B192C] outline-none focus:border-[#A7C7E7]"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block font-body text-xs text-[#334E68] mb-1.5">Caption</label>
          <textarea
            data-testid="admin-caption-input"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Make it cheeky and specific."
            rows={2}
            className="w-full rounded-2xl border border-[#EAF0F6] px-4 py-2.5 font-body text-sm text-[#0B192C] outline-none focus:border-[#A7C7E7] resize-none"
          />
        </div>

        <div className="md:col-span-2 flex justify-end">
          <button
            data-testid="admin-add-submit"
            disabled={uploading}
            className="rounded-full bg-[#0B192C] text-[#F7F9FC] px-6 py-2.5 font-body text-sm flex items-center gap-2 disabled:opacity-60"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {uploading ? "Uploading…" : "Add memory"}
          </button>
        </div>
      </form>
    </div>
  );
}

function BucketGroups({ items, token, onChanged }) {
  const grouped = useMemo(() => {
    const g = {};
    BUCKETS.forEach((b) => (g[b.key] = []));
    items.forEach((i) => {
      if (!g[i.bucket]) g[i.bucket] = [];
      g[i.bucket].push(i);
    });
    Object.keys(g).forEach((k) => g[k].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
    return g;
  }, [items]);

  return (
    <div className="space-y-10">
      {BUCKETS.map((b) => {
        const list = grouped[b.key] || [];
        if (!list.length) return null;
        return (
          <section key={b.key} data-testid={`bucket-group-${b.key}`}>
            <div className="mb-4">
              <p className="font-body text-xs uppercase tracking-[0.3em] text-[#7B9ACC]">{list.length} item{list.length === 1 ? "" : "s"}</p>
              <h3 className="mt-1 font-display font-bold text-2xl text-[#0B192C] tracking-tight">
                {b.label}
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {list.map((item, idx) => (
                <ItemEditor
                  key={item.id}
                  item={item}
                  token={token}
                  onChanged={onChanged}
                  bucketList={list}
                  index={idx}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function ItemEditor({ item, token, onChanged, bucketList, index }) {
  const [title, setTitle] = useState(item.title || "");
  const [caption, setCaption] = useState(item.caption || "");
  const [bucket, setBucket] = useState(item.bucket);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDirty(
      title !== (item.title || "") ||
      caption !== (item.caption || "") ||
      bucket !== item.bucket
    );
  }, [title, caption, bucket, item]);

  const save = async () => {
    setSaving(true);
    try {
      await updateMedia(token, item.id, { title, caption, bucket });
      toast.success("Saved.");
      onChanged();
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this memory?")) return;
    try {
      await deleteMedia(token, item.id);
      toast.success("Deleted.");
      onChanged();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const move = async (dir) => {
    const target = bucketList[index + dir];
    if (!target) return;
    try {
      await Promise.all([
        updateMedia(token, item.id, { sort_order: target.sort_order }),
        updateMedia(token, target.id, { sort_order: item.sort_order }),
      ]);
      onChanged();
    } catch {
      toast.error("Reorder failed.");
    }
  };

  return (
    <div className="rounded-3xl bg-white p-5 card-shadow border border-[#EAF0F6] flex gap-4" data-testid={`edit-item-${item.id}`}>
      <div className="w-32 shrink-0 aspect-[4/5] rounded-2xl overflow-hidden bg-[#EAF0F6]">
        {item.media_type === "video" ? (
          <video src={resolveUrl(item.url)} muted className="w-full h-full object-cover" />
        ) : (
          <img src={resolveUrl(item.url)} alt="" className="w-full h-full object-cover" loading="lazy" />
        )}
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title"
          className="w-full rounded-xl border border-[#EAF0F6] px-3 py-2 font-body text-sm text-[#0B192C] outline-none focus:border-[#A7C7E7]"
        />
        <textarea
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="Caption"
          rows={3}
          className="w-full rounded-xl border border-[#EAF0F6] px-3 py-2 font-body text-sm text-[#334E68] outline-none focus:border-[#A7C7E7] resize-none"
        />
        <div className="flex flex-wrap gap-2 items-center">
          <select
            value={bucket}
            onChange={(e) => setBucket(e.target.value)}
            className="rounded-full border border-[#EAF0F6] px-3 py-1 text-xs font-body text-[#0B192C] bg-white outline-none"
          >
            {BUCKETS.map((b) => (
              <option key={b.key} value={b.key}>{b.label}</option>
            ))}
          </select>
          <button
            onClick={() => move(-1)}
            disabled={index === 0}
            className="p-1.5 rounded-full border border-[#EAF0F6] text-[#334E68] hover:bg-[#F0F5FA] disabled:opacity-40"
            title="Move up"
          >
            <ArrowUp className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => move(1)}
            disabled={index === bucketList.length - 1}
            className="p-1.5 rounded-full border border-[#EAF0F6] text-[#334E68] hover:bg-[#F0F5FA] disabled:opacity-40"
            title="Move down"
          >
            <ArrowDown className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={async () => {
              try {
                await setHero(token, item.id);
                toast.success("Set as hero photo. Refresh the home page to see it.");
                onChanged();
              } catch { toast.error("Failed to set hero."); }
            }}
            disabled={item.media_type === "video"}
            className={`p-1.5 rounded-full border transition-colors ${
              item.is_hero
                ? "bg-[#0B192C] text-[#A7C7E7] border-[#0B192C]"
                : "border-[#EAF0F6] text-[#334E68] hover:bg-[#F0F5FA]"
            } disabled:opacity-40`}
            title={item.is_hero ? "This is the hero" : "Set as hero photo"}
          >
            <Star className="w-3.5 h-3.5" fill={item.is_hero ? "#A7C7E7" : "none"} />
          </button>
          <button
            onClick={del}
            className="p-1.5 rounded-full border border-red-100 text-red-500 hover:bg-red-50 ml-auto"
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={save}
            disabled={!dirty || saving}
            className={`rounded-full px-3 py-1.5 text-xs font-body flex items-center gap-1.5 transition-colors ${
              dirty
                ? "bg-[#0B192C] text-[#F7F9FC]"
                : "bg-[#F0F5FA] text-[#334E68] cursor-not-allowed"
            }`}
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
